import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ApiServiceAdmin from '../../services/ApiServiceAdmin.js';
import { useAssociation } from '../../contexts/AssociationContext';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';

// Composants d'icônes typés pour éviter les erreurs TypeScript
const EyeIcon: React.FC<{ size?: number }> = ({ size = 20 }) => {
  const IconComponent = AiOutlineEye as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle' }} />;
};

const EyeInvisibleIcon: React.FC<{ size?: number }> = ({ size = 20 }) => {
  const IconComponent = AiOutlineEyeInvisible as any;
  return <IconComponent size={size} style={{ display: 'inline-block', verticalAlign: 'middle' }} />;
};

interface ParentLoginProps {
  translations: any;
  currentLanguage: string;
  isRTL: boolean;
  onBackHome: () => void;
  onLoginSuccess?: () => void;
}

const ParentLogin: React.FC<ParentLoginProps> = ({ translations, currentLanguage, isRTL, onBackHome, onLoginSuccess }) => {
  const { association, getName } = useAssociation();
  const logoUrl      = association?.logoUrl     ?? '/images/logo.png';
  const theme        = association?.theme;
  const primaryColor = theme?.primary     ?? '#2E7D32';
  const primaryText  = theme?.primaryText ?? '#FFFFFF';
  const accent       = theme?.accent      ?? primaryColor;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const apiService = ApiServiceAdmin.getInstance();

  // États pour la récupération de mot de passe
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [isLoadingForgot, setIsLoadingForgot] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  // Fonction de traduction
  const t = (key: string) => {
    return (translations[currentLanguage as keyof typeof translations] as any)?.[key] || key;
  };

  // Fonction pour bloquer les caractères arabes
  const blockArabicInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    if (arabicPattern.test(e.key)) {
      e.preventDefault();
    }
  };

  // Fonction pour bloquer le collage de caractères arabes
  const blockArabicPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    if (arabicPattern.test(pastedText)) {
      e.preventDefault();
      alert(t('latinCharsOnly') || 'يُسمح فقط بالأحرف اللاتينية');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!username || !password) {
      setErrorMessage(t('fillAllFields'));
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await apiService.login(username, password, 'parent');
      
      if (result.success) {
        if (onLoginSuccess) {
          onLoginSuccess();
        } else {
          console.warn('⚠️ onLoginSuccess non défini');
        }
      } else {
        setErrorMessage(result.message || t('loginError'));
      }
    } catch (error) {
      setErrorMessage(t('connectionError'));
      console.error('Erreur de connexion:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    
    if (!forgotEmail && !forgotUsername && !forgotPhone) {
      setForgotError(t('forgotPasswordError'));
      return;
    }

    setIsLoadingForgot(true);
    
    try {
      const result = await apiService.resendCredentials('parent', {
        email: forgotEmail || undefined,
        username: forgotUsername || undefined,
        phone: forgotPhone || undefined,
        language: currentLanguage
      });
      
      if (result.success) {
        setForgotSuccess(result.message || t('forgotPasswordSuccess'));
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotEmail('');
          setForgotUsername('');
          setForgotPhone('');
          setForgotSuccess('');
        }, 3000);
      } else {
        setForgotError(result.error || t('forgotPasswordError'));
      }
    } catch (error) {
      setForgotError(t('connectionError'));
      console.error('Erreur de récupération:', error);
    } finally {
      setIsLoadingForgot(false);
    }
  };

  return (
    <>
      <style>{`
        .forgot-password-input::placeholder {
          font-size: 0.75rem;
          color: #9ca3af;
          opacity: 0.6;
        }
      `}</style>
    <div style={{
      fontFamily: 'Cairo, sans-serif',
        background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 50%, #A5D6A7 100%)',
      minHeight: '100vh',
      display: 'flex',
      direction: isRTL ? 'rtl' : 'ltr',
      padding: 0,
      overflow: 'hidden'
    }}>
      {/* Image de gauche - Responsive */}
      <div style={{
        width: windowWidth < 1024 ? '40%' : '50%',
        minWidth: windowWidth < 768 ? '0' : '300px',
        height: '100vh',
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/student-login.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        flexShrink: 0,
        display: windowWidth < 768 ? 'none' : 'block',
        position: 'relative'
      }}>
        {/* Overlay pour améliorer la lisibilité */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(102, 187, 106, 0.1) 100%)',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 40px',
          textAlign: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Message de bienvenue en haut */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            padding: '12px 18px',
            fontSize: '0.9rem',
            color: '#FFFFFF',
            fontWeight: 'bold',
            backdropFilter: 'blur(10px)',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            🎓 {t('parentWelcome')}
          </div>
          
          {/* Contenu principal au centre */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <h3 style={{
              color: '#FFFFFF',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '20px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
            }}>
              {t('parentPlatform')}
            </h3>
            <p style={{
              color: '#FFFFFF',
              fontSize: '1.1rem',
              lineHeight: '1.6',
              textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
              maxWidth: '300px',
              marginBottom: '30px'
            }}>
              {t('parentDescription')}
            </p>
            
            <h4 style={{
              color: '#FFFFFF',
              fontSize: '1.2rem',
              fontWeight: '600',
              marginBottom: '20px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              textAlign: 'center'
            }}>
              {t('parentServices')}
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              maxWidth: '400px'
            }}>
              {[
                t('parentService1'),
                t('parentService2'),
                t('parentService3'),
                t('parentService4')
              ].map((feature, index) => (
                <div key={index} style={{
                  color: '#FFFFFF',
                  fontSize: '0.9rem',
                  padding: '10px 12px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(5px)'
                }}>
                  <span style={{
                  color: accent,
                  fontWeight: 'bold',
                  marginLeft: '8px',
                  fontSize: '1rem'
                }}>✓</span>
                  {feature}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire de connexion */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: windowWidth < 768 ? '20px' : '60px 40px',
        minHeight: '100vh',
        background: windowWidth < 768 ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 50%, #A5D6A7 100%)' : 'transparent'
      }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            background: 'transparent',
            padding: '40px',
            borderRadius: '0',
            boxShadow: 'none',
            width: '100%',
            maxWidth: '450px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >

        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img 
            src={logoUrl}
            alt={getName(currentLanguage)} 
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'block',
              objectFit: 'cover',
              border: `3px solid ${accent}`
            }}
          />
          <div style={{
            color: '#FFFFFF',
            fontSize: '1.2rem',
            fontWeight: '600',
            marginBottom: '8px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            opacity: 0.9
          }}>
            {t('associationName')}
          </div>
          <h1 style={{
            color: '#FFFFFF',
            fontSize: '1.8rem',
            fontWeight: '700',
            marginBottom: '10px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}>
            {t('parentTitle')}
          </h1>
          <h2 style={{
            color: '#FFFFFF',
            fontSize: '1rem',
            marginBottom: '20px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
          }}>
            {t('parentSubtitle')}
          </h2>
        </div>



        <form onSubmit={handleSubmit}>
          {errorMessage && (
            <div style={{
              background: 'rgba(220, 53, 69, 0.1)',
              border: '1px solid #dc3545',
              color: '#dc3545',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {errorMessage}
            </div>
          )}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#FFFFFF',
              fontWeight: '600',
              marginBottom: '8px',
              fontSize: '0.9rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              textAlign: isRTL ? 'right' : 'left',
              direction: isRTL ? 'rtl' : 'ltr'
            }}>
              {t('username')}
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={blockArabicInput}
              onPaste={blockArabicPaste}
              required
              placeholder={t('enterUsername')}
              dir="ltr"
              style={{
                width: '100%',
                padding: '15px',
                border: '2px solid #E9ECEF',
                borderRadius: '10px',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                background: '#F8F9FA',
                boxSizing: 'border-box',
                direction: 'ltr',
                textAlign: 'left'
              }}
              onFocus={(e) => {
                e.target.style.outline = 'none';
                e.target.style.borderColor = accent;
                e.target.style.background = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E9ECEF';
                e.target.style.background = '#F8F9FA';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#FFFFFF',
              fontWeight: '600',
              marginBottom: '8px',
              fontSize: '0.9rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              textAlign: isRTL ? 'right' : 'left',
              direction: isRTL ? 'rtl' : 'ltr'
            }}>
              {t('password')}
            </label>
            <div style={{ position: 'relative' }}>
            <input
                type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={blockArabicInput}
              onPaste={blockArabicPaste}
              required
              placeholder={t('enterPassword')}
              autoComplete="current-password"
              dir="ltr"
              style={{
                width: '100%',
                padding: '15px',
                paddingRight: '50px',
                paddingLeft: '15px',
                border: '2px solid #E9ECEF',
                borderRadius: '10px',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                background: '#F8F9FA',
                boxSizing: 'border-box',
                direction: 'ltr',
                textAlign: 'left'
              }}
                onFocus={(e) => {
                e.target.style.outline = 'none';
                e.target.style.borderColor = accent;
                e.target.style.background = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E9ECEF';
                e.target.style.background = '#F8F9FA';
                e.target.style.boxShadow = 'none';
              }}
            />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6C757D',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6C757D';
                }}
              >
                {showPassword ? (
                  <EyeInvisibleIcon size={20} />
                ) : (
                  <EyeIcon size={20} />
                )}
              </button>
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '25px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: accent
                }}
              />
              <label htmlFor="remember" style={{
                color: '#6C757D',
                fontSize: '0.9rem',
                cursor: 'pointer',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('rememberMe')}
              </label>
            </div>
            <button type="button" onClick={() => setShowForgotPassword(true)} style={{
              color: '#FFFFFF',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'color 0.3s ease',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}>
              {t('forgotPassword')}
            </button>
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '15px',
              background: accent,
              color: primaryText,
              border: 'none',
              borderRadius: '10px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              marginBottom: '20px',
              opacity: isLoading ? 0.7 : 1,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {isLoading ? t('loading') : t('login')}
          </motion.button>
        </form>

        <div style={{
          textAlign: 'center',
          margin: '25px 0',
          position: 'relative',
          color: '#6C757D',
          fontSize: '0.9rem'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            background: '#E9ECEF',
            zIndex: 1
          }} />
          <span style={{
            background: 'white',
            padding: '0 15px',
            position: 'relative',
            zIndex: 2,
            direction: isRTL ? 'rtl' : 'ltr'
          }}>
            {t('or')}
          </span>
        </div>

        {/* <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button type="button" onClick={() => {}} style={{
            color: '#FFFFFF',
            textDecoration: 'none',
            fontWeight: '600',
            transition: 'color 0.3s ease',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            background: 'none',
            border: 'none',
            cursor: 'pointer'
          }}>
            {t('noAccount')}
          </button>
        </div> */}

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button type="button" onClick={onBackHome} style={{
            color: '#FFFFFF',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: '600',
            transition: 'color 0.3s ease',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            background: 'none',
            border: 'none',
            cursor: 'pointer'
          }}>
            {t('backHome')}
          </button>
        </div>
        </motion.div>
      </div>

      {/* Modal de récupération de mot de passe */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => {
          if (!isLoadingForgot) {
            setShowForgotPassword(false);
            setForgotError('');
            setForgotSuccess('');
            setForgotEmail('');
            setForgotUsername('');
            setForgotPhone('');
          }
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                color: '#1f2937',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0
              }}>
                {t('forgotPassword')}
              </h2>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotError('');
                  setForgotSuccess('');
                  setForgotEmail('');
                  setForgotUsername('');
                  setForgotPhone('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <p style={{
              color: '#6b7280',
              fontSize: '0.9rem',
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              {t('forgotPasswordDescription')}
            </p>

            <form onSubmit={handleForgotPassword}>
              {forgotError && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '0.9rem'
                }}>
                  {forgotError}
                </div>
              )}

              {forgotSuccess && (
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#16a34a',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '0.9rem'
                }}>
                  {forgotSuccess}
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.9rem',
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}>
                  {t('email')} ({t('optional')})
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder={t('enterEmail')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E9ECEF',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  className="forgot-password-input"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.9rem',
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}>
                  {t('username')} ({t('optional')})
                </label>
                <input
                  type="text"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  onKeyPress={blockArabicInput}
                  onPaste={blockArabicPaste}
                  placeholder={t('enterUsername')}
                  dir="ltr"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E9ECEF',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                  className="forgot-password-input"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.9rem',
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}>
                  {t('phone')} ({t('optional')})
                </label>
                <input
                  type="tel"
                  value={forgotPhone}
                  onChange={(e) => setForgotPhone(e.target.value)}
                  placeholder={t('enterPhone')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E9ECEF',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  className="forgot-password-input"
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotError('');
                    setForgotSuccess('');
                    setForgotEmail('');
                    setForgotUsername('');
                    setForgotPhone('');
                  }}
                  disabled={isLoadingForgot}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isLoadingForgot ? 'not-allowed' : 'pointer',
                    opacity: isLoadingForgot ? 0.6 : 1
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoadingForgot}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: primaryColor,
                    color: primaryText,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isLoadingForgot ? 'not-allowed' : 'pointer',
                    opacity: isLoadingForgot ? 0.6 : 1
                  }}
                >
                  {isLoadingForgot ? t('loading') : t('send')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
    </>
  );
};

export default ParentLogin;
